import React, { useEffect, useState, useRef } from 'react';
import { firestore, auth } from '../firebase/config';import { collection, getDocs, doc, getDoc, deleteDoc, updateDoc, addDoc, query, where, setDoc, writeBatch } from 'firebase/firestore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useToast } from '../components/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Invoice from './Invoice';
import MemberDropdownPortal from '../components/MemberDropdownPortal';
import ChatIcon from '../components/ChatIcon';
import { useAuthState } from 'react-firebase-hooks/auth';
import { migratePasswords, checkPasswordMigration } from '../utils/migratePasswords';
import { decryptForAdmin } from '../utils/passwordUtils';
import { getPakistanTime, getPakistanDate, calculateDuration } from '../utils/timeUtils';
import QuizModal from '../components/QuizModal';

// Add this style block at the top or bottom of the file (outside the component):
/*
<style>
.card-action-btn {
  flex: 1;
  padding: 4px 16px;
  margin: 0 2px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  color: #fff;
  background: #2563eb;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.2s, transform 0.2s;
  outline: none;
}
.card-action-btn.edit {
  background: #6b7280;
}
.card-action-btn.message {
  background: #3b82f6;
}
.card-action-btn:hover {
  background: #1e40af;
  transform: scale(1.05);
  cursor: pointer;
}
.card-action-btn.edit:hover {
  background: #374151;
}
.card-action-btn.message:hover {
  background: #1d4ed8;
}
</style>
*/

function AdminDashboard({ userRole }) {
  const [employees, setEmployees] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(userRole === 'manager' ? 'students' : 'dashboard'); // 'dashboard', 'members', 'students', 'finance', 'courses', 'invoice'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [studentFeeFilter, setStudentFeeFilter] = useState('all');
  const [employeeAttendanceFilter, setEmployeeAttendanceFilter] = useState('all');
  const [employeeSalaryFilter, setEmployeeSalaryFilter] = useState('all');
  const [studentAttendanceFilter, setStudentAttendanceFilter] = useState('all');
  const [studentSearch, setStudentSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageStudentId, setMessageStudentId] = useState(null);
  const [messageEmployeeId, setMessageEmployeeId] = useState(null);
  const [messageRecipientType, setMessageRecipientType] = useState(null); // 'student' or 'employee'
  const [messageText, setMessageText] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [expenseDesc, setExpenseDesc] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseError, setExpenseError] = useState('');
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [clientDetailsModalOpen, setClientDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [courseError, setCourseError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showOutlineModal, setShowOutlineModal] = useState(false);
  const [outlineInput, setOutlineInput] = useState("");
  const [outlineLoading, setOutlineLoading] = useState(false);
  // Add local state for checked outlines in the modal
  const [adminCheckedOutlines, setAdminCheckedOutlines] = useState([]);
  const { showSuccess, showError } = useToast();
  const [showDeleteStudentModal, setShowDeleteStudentModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [dropdownAnchorRect, setDropdownAnchorRect] = useState(null);
  const memberBtnRef = useRef(null);
  const [employeeTab, setEmployeeTab] = useState('all'); // 'all', 'internee', 'manager'
  const [currentUser] = useAuthState(auth);
  const today = getPakistanDate();
  
  // Quiz modal state
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [selectedQuizCourse, setSelectedQuizCourse] = useState(null);
  
  // Client management state
  const [clients, setClients] = useState([]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [newClient, setNewClient] = useState({
    name: '',
    projectName: '',
    projectReceiveDate: '',
    description: '',
    deadline: '',
    projectCost: '',
    assignedTo: '',
    developerPay: '',
    notes: ''
  });
  const [editingClient, setEditingClient] = useState(null);
  const [showDisableStudentModal, setShowDisableStudentModal] = useState(false);
  const [showDisableEmployeeModal, setShowDisableEmployeeModal] = useState(false);
  const [studentToDisable, setStudentToDisable] = useState(null);
  const [employeeToDisable, setEmployeeToDisable] = useState(null);
  
  // Password migration state
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  
  // Pagination state for students
  const [currentStudentPage, setCurrentStudentPage] = useState(1);
  const studentsPerPage = 20;
  const [showAllStudents, setShowAllStudents] = useState(false);
  
  // Pagination state for members/employees
  const [currentMemberPage, setCurrentMemberPage] = useState(1);
  const membersPerPage = 20;
  const [showAllMembers, setShowAllMembers] = useState(false);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentStudentPage(1);
    setShowAllStudents(false);
  }, [studentFeeFilter, studentAttendanceFilter, studentSearch]);
  
  // Reset member pagination when filters change
  useEffect(() => {
    setCurrentMemberPage(1);
    setShowAllMembers(false);
  }, [employeeAttendanceFilter, employeeSalaryFilter, employeeSearch, employeeTab]);

  // Monthly cleanup: delete attendance older than 30 days on the 1st of each month
  useEffect(() => {
    const runMonthlyAttendanceCleanup = async () => {
      try {
        // Determine Pakistan-local dates
        const now = new Date();
        const todayPk = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' }); // YYYY-MM-DD
        const dayOfMonth = parseInt(
          now.toLocaleDateString('en-PK', { day: '2-digit', timeZone: 'Asia/Karachi' }),
          10
        );

        // Only run on the 1st of the month
        if (dayOfMonth !== 1) return;

        // Ensure we run once per month using maintenance doc
        const currentMonthKey = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi', year: 'numeric', month: '2-digit' }); // YYYY-MM
        const maintenanceRef = doc(firestore, 'maintenance', 'monthlyAttendanceCleanup');
        const maintenanceSnap = await getDoc(maintenanceRef);
        const lastRunMonth = maintenanceSnap.exists() ? maintenanceSnap.data().lastRunMonth : null;
        if (lastRunMonth === currentMonthKey) return; // already ran this month

        // Compute cutoff date string (30 days ago in PK timezone)
        const cutoffDate = new Date(now);
        cutoffDate.setDate(cutoffDate.getDate() - 30);
        const cutoffStr = cutoffDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' }); // YYYY-MM-DD

        // Helper to delete from a collection where date <= cutoff
        const deleteOlderThan = async (collectionName) => {
          const qOld = query(collection(firestore, collectionName), where('date', '<=', cutoffStr));
          const snapshot = await getDocs(qOld);
          if (snapshot.empty) return 0;

          let deleted = 0;
          let opsInBatch = 0;
          let batch = writeBatch(firestore);
          for (const d of snapshot.docs) {
            batch.delete(d.ref);
            opsInBatch += 1;
            deleted += 1;
            if (opsInBatch >= 450) { // stay safely under limit 500
              await batch.commit();
              batch = writeBatch(firestore);
              opsInBatch = 0;
            }
          }
          if (opsInBatch > 0) {
            await batch.commit();
          }
          return deleted;
        };

        const deletedEmployees = await deleteOlderThan('employeeAttendance');
        const deletedStudents = await deleteOlderThan('studentAttendance');

        // Mark as executed for this month
        await setDoc(maintenanceRef, { lastRunMonth: currentMonthKey, lastRunAt: todayPk, deletedEmployees, deletedStudents }, { merge: true });

        // Optional: notify admin
        if ((deletedEmployees || deletedStudents) && typeof window !== 'undefined') {
          try { showSuccess(`Attendance cleanup complete. Deleted: Employees ${deletedEmployees}, Students ${deletedStudents}.`); } catch {}
        }
      } catch (e) {
        try { showError('Monthly attendance cleanup failed. Check console.'); } catch {}
        console.error('Monthly attendance cleanup error:', e);
      }
    };

    runMonthlyAttendanceCleanup();
  }, [showSuccess, showError]);

  const fetchAllData = async () => {
    setLoading(true);
    
    // Fetch employees and their attendance
    const employeeSnapshot = await getDocs(collection(firestore, 'employees'));
    const employeeList = employeeSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setEmployees(employeeList);

    // Fetch students
    const studentSnapshot = await getDocs(collection(firestore, 'students'));
    const studentList = studentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setStudents(studentList);

    // Fetch attendance data from separate collections
    const employeeAttendanceSnapshot = await getDocs(collection(firestore, 'employeeAttendance'));
    const studentAttendanceSnapshot = await getDocs(collection(firestore, 'studentAttendance'));
    
    const employeeAttendanceData = employeeAttendanceSnapshot.docs.map(doc => doc.data());
    const studentAttendanceData = studentAttendanceSnapshot.docs.map(doc => doc.data());

    // Build attendance map for employees
    const attendanceData = {};
    for (let emp of employeeList) {
      // Try UID first, then document ID as fallback
      let empAttendance = employeeAttendanceData.filter(a => a.uid === emp.uid);
      if (empAttendance.length === 0 && emp.uid !== emp.id) {
        empAttendance = employeeAttendanceData.filter(a => a.uid === emp.id);
      }
      attendanceData[emp.uid] = empAttendance;
    }

    // Add student attendance to the same map using pre-fetched data (avoid N+1 reads)
    for (let student of studentList) {
      // Try UID first, then document ID as fallback
      let studentAttendance = studentAttendanceData.filter(a => a.uid === student.uid);
      if (studentAttendance.length === 0 && student.uid !== student.id) {
        studentAttendance = studentAttendanceData.filter(a => a.uid === student.id);
      }
      attendanceData[student.uid] = studentAttendance;
    }

    setAttendanceMap(attendanceData);
    
    // Fetch clients
    await fetchClients();
    
    setLoading(false);
  };

  const fetchEmployees = async () => {
    setLoading(true);
    const employeeSnapshot = await getDocs(collection(firestore, 'employees'));
    const employeeList = employeeSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort by newest first (assuming createdAt field, fallback to id for newer entries)
    const sortedEmployees = employeeList.sort((a, b) => {
      // If both have createdAt, sort by that
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      // If only one has createdAt, prioritize the one with it
      if (a.createdAt && !b.createdAt) return -1;
      if (!a.createdAt && b.createdAt) return 1;
      // Fallback to sorting by id (newer ids first)
      return b.id.localeCompare(a.id);
    });
    setEmployees(sortedEmployees);

    // Fetch attendance for each employee
    const attendanceData = {};
    for (let emp of employeeList) {
      const attendanceSnapshot = await getDocs(collection(firestore, 'employeeAttendance'));
      const allEmpAttendance = attendanceSnapshot.docs.map(doc => doc.data());
      
      // Try UID first, then document ID as fallback
      let empAttendance = allEmpAttendance.filter(a => a.uid === emp.uid);
      if (empAttendance.length === 0 && emp.uid !== emp.id) {
        empAttendance = allEmpAttendance.filter(a => a.uid === emp.id);
      }

      attendanceData[emp.uid] = empAttendance;
    }
    setAttendanceMap(attendanceData);
    setLoading(false);
  };

  const fetchStudents = async () => {
    const studentSnapshot = await getDocs(collection(firestore, 'students'));
    const studentList = studentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort by newest first (assuming createdAt field, fallback to id for newer entries)
    const sortedStudents = studentList.sort((a, b) => {
      // If both have createdAt, sort by that
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      // If only one has createdAt, prioritize the one with it
      if (a.createdAt && !b.createdAt) return -1;
      if (!a.createdAt && b.createdAt) return 1;
      // Fallback to sorting by id (newer ids first)
      return b.id.localeCompare(a.id);
    });
    setStudents(sortedStudents);

    // Fetch attendance for each student (same logic as employees)
    const currentAttendanceData = { ...attendanceMap }; // preserve existing attendance data
    for (let student of studentList) {
      const attendanceSnapshot = await getDocs(collection(firestore, 'studentAttendance'));
      const allStudentAttendance = attendanceSnapshot.docs.map(doc => doc.data());
      
      // Try UID first, then document ID as fallback
      let studentAttendance = allStudentAttendance.filter(a => a.uid === student.uid);
      if (studentAttendance.length === 0 && student.uid !== student.id) {
        studentAttendance = allStudentAttendance.filter(a => a.uid === student.id);
      }

      currentAttendanceData[student.uid] = studentAttendance;
    }
    setAttendanceMap(currentAttendanceData);
  };

  const fetchExpenses = async () => {
    try {
      const expensesSnapshot = await getDocs(collection(firestore, 'expenses'));
      const expensesList = expensesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExpenses(expensesList);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const coursesSnapshot = await getDocs(collection(firestore, 'courses'));
      const coursesList = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCourses(coursesList);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Client management functions
  const fetchClients = async () => {
    try {
      const querySnapshot = await getDocs(collection(firestore, 'clients'));
      const clientsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const addClient = async () => {
    try {
      if (!newClient.name || !newClient.projectName) {
        showError('Please fill in all required fields');
        return;
      }

      const projectCost = parseFloat(newClient.projectCost) || 0;
      const developerPay = parseFloat(newClient.developerPay) || 0;

      const clientData = {
        name: newClient.name,
        projectName: newClient.projectName,
        projectReceiveDate: newClient.projectReceiveDate,
        description: newClient.description,
        deadline: newClient.deadline,
        projectCost: projectCost,
        assignedTo: newClient.assignedTo,
        developerPay: developerPay,
        notes: newClient.notes,
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.email || 'admin',
        profit: projectCost - developerPay
      };

      // Add client to clients collection
      const clientRef = await addDoc(collection(firestore, 'clients'), clientData);
      
      // Note: Project costs are tracked in client records but not added to expense history
      // Only developer payments are added as expenses to maintain clean expense tracking

      // Add developer pay as expense to finance system
      if (developerPay > 0) {
        const expenseData = {
          desc: `Developer Payment: ${newClient.assignedTo || 'Developer'} - ${newClient.projectName} (Client: ${newClient.name})`,
          amount: developerPay,
          type: 'expense',
          category: 'developer_payment',
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.email || 'admin',
          clientId: clientRef.id,
          clientName: newClient.name,
          projectName: newClient.projectName,
          developerName: newClient.assignedTo || 'Developer'
        };
        await addDoc(collection(firestore, 'expenses'), expenseData);
      }

      showSuccess('Client added successfully! Finance records updated.');
      setClientModalOpen(false);
      setNewClient({
        name: '',
        projectName: '',
        projectReceiveDate: '',
        description: '',
        deadline: '',
        projectCost: '',
        assignedTo: '',
        developerPay: '',
        notes: ''
      });
      fetchClients();
      fetchExpenses(); // Refresh expenses to show new entries
    } catch (error) {
      showError('Error adding client');
      console.error('Error adding client:', error);
    }
  };

  const updateClient = async () => {
    try {
      if (!newClient.name || !newClient.projectName) {
        showError('Please fill in all required fields');
        return;
      }

      const projectCost = parseFloat(newClient.projectCost) || 0;
      const developerPay = parseFloat(newClient.developerPay) || 0;

      const clientData = {
        name: newClient.name,
        projectName: newClient.projectName,
        projectReceiveDate: newClient.projectReceiveDate,
        description: newClient.description,
        deadline: newClient.deadline,
        projectCost: projectCost,
        assignedTo: newClient.assignedTo,
        developerPay: developerPay,
        notes: newClient.notes,
        id: editingClient.id,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.email || 'admin',
        profit: projectCost - developerPay
      };

      // Update client data
      await updateDoc(doc(firestore, 'clients', editingClient.id), clientData);

      // Update finance records - first delete old entries, then add new ones
      try {
        // Delete old finance entries for this client
        const expensesRef = collection(firestore, 'expenses');
        const q = query(expensesRef, where('clientId', '==', editingClient.id));
        const querySnapshot = await getDocs(q);
        
        const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        // Note: Project costs are tracked in client records but not added to expense history
        // Only developer payments are added as expenses to maintain clean expense tracking

        // Add new developer pay as expense
        if (developerPay > 0) {
          const expenseData = {
            desc: `Developer Payment: ${newClient.assignedTo || 'Developer'} - ${newClient.projectName} (Client: ${newClient.name})`,
            amount: developerPay,
            type: 'expense',
            category: 'developer_payment',
            createdAt: new Date().toISOString(),
            createdBy: currentUser?.email || 'admin',
            clientId: editingClient.id,
            clientName: newClient.name,
            projectName: newClient.projectName,
            developerName: newClient.assignedTo || 'Developer'
          };
          await addDoc(collection(firestore, 'expenses'), expenseData);
        }
      } catch (error) {
        console.error('Error updating finance records:', error);
      }

      showSuccess('Client updated successfully! Finance records updated.');
      setClientModalOpen(false);
      setEditingClient(null);
      setNewClient({
        name: '',
        projectName: '',
        projectReceiveDate: '',
        description: '',
        deadline: '',
        projectCost: '',
        assignedTo: '',
        developerPay: '',
        notes: ''
      });
      fetchClients();
      fetchExpenses(); // Refresh expenses to show updated entries
    } catch (error) {
      showError('Error updating client');
      console.error('Error updating client:', error);
    }
  };

  const deleteClient = async (clientId) => {
    try {
      // Delete client
      await deleteDoc(doc(firestore, 'clients', clientId));
      
      // Delete associated finance records
      try {
        const expensesRef = collection(firestore, 'expenses');
        const q = query(expensesRef, where('clientId', '==', clientId));
        const querySnapshot = await getDocs(q);
        
        const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
      } catch (error) {
        console.error('Error deleting finance records:', error);
      }
      
      showSuccess('Client deleted successfully! Finance records removed.');
      fetchClients();
      fetchExpenses(); // Refresh expenses
    } catch (error) {
      showError('Error deleting client');
      console.error('Error deleting client:', error);
    }
  };

  const openClientModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setNewClient({
        name: client.name,
        projectName: client.projectName || '',
        projectReceiveDate: client.projectReceiveDate || '',
        description: client.description || '',
        deadline: client.deadline || '',
        projectCost: client.projectCost?.toString() || '',
        assignedTo: client.assignedTo || '',
        developerPay: client.developerPay?.toString() || '',
        notes: client.notes || ''
      });
    } else {
      setEditingClient(null);
      setNewClient({
        name: '',
        projectName: '',
        projectReceiveDate: '',
        description: '',
        deadline: '',
        projectCost: '',
        assignedTo: '',
        developerPay: '',
        notes: ''
      });
    }
    setClientModalOpen(true);
  };

  const closeClientModal = () => {
    setClientModalOpen(false);
    setEditingClient(null);
    setNewClient({
      name: '',
      projectName: '',
      projectReceiveDate: '',
      description: '',
      deadline: '',
      projectCost: '',
      assignedTo: '',
      developerPay: '',
      notes: ''
    });
  };

  const addCourse = async () => {
    if (!newCourseName.trim()) {
      setCourseError('Please enter a course name.');
      return;
    }
    
    try {
      const newCourse = {
        name: newCourseName.trim(),
        createdAt: new Date().toISOString()
      };
      
      await addDoc(collection(firestore, 'courses'), newCourse);
      await fetchCourses(); // Refresh the courses list
      setNewCourseName('');
      setCourseModalOpen(false);
      setCourseError('');
    } catch (error) {
      console.error('Error adding course:', error);
      setCourseError('Failed to add course. Please try again.');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      await deleteDoc(doc(firestore, 'courses', courseId));
      setCourses(courses.filter(course => course.id !== courseId));
      showSuccess('Course deleted successfully');
    } catch (error) {
      showError('Failed to delete course. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(firestore, 'employees', id));
    await deleteDoc(doc(firestore, 'users', id));
    fetchAllData();
  };

  const handleDisableEmployee = async (employee) => {
    try {
      // Update employees collection
      await updateDoc(doc(firestore, 'employees', employee.id), {
        disabled: true,
        disabledAt: new Date().toISOString(),
        disabledBy: currentUser?.email || 'admin'
      });
      
      // Also update users collection if the employee exists there
      try {
        const usersRef = collection(firestore, 'users');
        const q = query(usersRef, where('contactNumber', '==', employee.contactNumber));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          await updateDoc(doc(firestore, 'users', userDoc.id), {
            disabled: true,
            disabledAt: new Date().toISOString(),
            disabledBy: currentUser?.email || 'admin'
          });
        }
      } catch (error) {
        
      }
      
      showSuccess('Employee disabled successfully!');
      fetchEmployees();
      setShowDisableEmployeeModal(false);
      setEmployeeToDisable(null);
    } catch (error) {
      showError('Error disabling employee');
      console.error('Error disabling employee:', error);
    }
  };

  const handleEnableEmployee = async (employee) => {
    try {
      // Update employees collection
      await updateDoc(doc(firestore, 'employees', employee.id), {
        disabled: false,
        enabledAt: new Date().toISOString(),
        enabledBy: currentUser?.email || 'admin'
      });
      
      // Also update users collection if the employee exists there
      try {
        const usersRef = collection(firestore, 'users');
        const q = query(usersRef, where('contactNumber', '==', employee.contactNumber));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          await updateDoc(doc(firestore, 'users', userDoc.id), {
            disabled: false,
            enabledAt: new Date().toISOString(),
            enabledBy: currentUser?.email || 'admin'
          });
        }
      } catch (error) {
        
      }
      
      showSuccess('Employee enabled successfully!');
      fetchEmployees();
    } catch (error) {
      showError('Error enabling employee');
      console.error('Error enabling employee:', error);
    }
  };

  const handleDeleteStudent = async (id) => {
    await deleteDoc(doc(firestore, 'students', id));
    fetchAllData();
  };

  const handleDisableStudent = async (student) => {
    try {
      // Update students collection
      await updateDoc(doc(firestore, 'students', student.id), {
        disabled: true,
        disabledAt: new Date().toISOString(),
        disabledBy: currentUser?.email || 'admin'
      });
      
      // Also update users collection if the student exists there
      try {
        const usersRef = collection(firestore, 'users');
        const q = query(usersRef, where('contactNumber', '==', student.contactNumber));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          await updateDoc(doc(firestore, 'users', userDoc.id), {
            disabled: true,
            disabledAt: new Date().toISOString(),
            disabledBy: currentUser?.email || 'admin'
          });
        }
      } catch (error) {
        // users collection not updated or user not found; ignoring in UI
      }
      
      showSuccess('Student disabled successfully!');
      fetchStudents();
      setShowDisableStudentModal(false);
      setStudentToDisable(null);
    } catch (error) {
      showError('Error disabling student');
      console.error('Error disabling student:', error);
    }
  };

  const handleEnableStudent = async (student) => {
    try {
      // Update students collection
      await updateDoc(doc(firestore, 'students', student.id), {
        disabled: false,
        enabledAt: new Date().toISOString(),
        enabledBy: currentUser?.email || 'admin'
      });
      
      // Also update users collection if the student exists there
      try {
        const usersRef = collection(firestore, 'users');
        const q = query(usersRef, where('contactNumber', '==', student.contactNumber));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          await updateDoc(doc(firestore, 'users', userDoc.id), {
            disabled: false,
            enabledAt: new Date().toISOString(),
            enabledBy: currentUser?.email || 'admin'
          });
        }
      } catch (error) {
        console.log('User not found in users collection or error updating users collection:', error);
      }
      
      showSuccess('Student enabled successfully!');
      fetchStudents();
    } catch (error) {
      showError('Error enabling student');
      console.error('Error enabling student:', error);
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await deleteDoc(doc(firestore, 'expenses', id));
      setExpenses(expenses.filter(expense => expense.id !== id));
      showSuccess('Expense deleted successfully');
    } catch (error) {
      showError('Failed to delete expense. Please try again.');
    }
  };

  const handleLogout = () => {
    // Clear custom authentication
    localStorage.removeItem('customUser');
    localStorage.removeItem('userRole');
    
    // Sign out from Firebase Auth
    signOut(auth);
    
    navigate('/login');
  };

  // Password migration functions
  const handlePasswordMigration = async () => {
    setMigrationLoading(true);
    try {
      const result = await migratePasswords();
      if (result.success) {
        showSuccess(`Password migration completed! ${result.usersMigrated} users, ${result.studentsMigrated} students, ${result.employeesMigrated} employees migrated.`);
        setMigrationStatus('completed');
      } else {
        showError(`Migration failed: ${result.error}`);
        setMigrationStatus('failed');
      }
    } catch (error) {
      showError(`Migration error: ${error.message}`);
      setMigrationStatus('failed');
    }
    setMigrationLoading(false);
  };

  const checkMigrationStatus = async () => {
    try {
      const status = await checkPasswordMigration();
      setMigrationStatus(status.needsMigration ? 'needed' : 'completed');
    } catch (error) {
      console.error('Error checking migration status:', error);
    }
  };

  // Check migration status on component mount
  useEffect(() => {
    checkMigrationStatus();
  }, []);

  const getTodayAttendance = (empId) => {
    const today = getPakistanDate();
    const attendance = attendanceMap[empId] || [];
    return attendance.find(a => a.date === today);
  };

  const getAttendanceStatus = (empId) => {
    const todayAttendance = getTodayAttendance(empId);
    if (!todayAttendance) return 'Absent';
    if (todayAttendance.checkIn && todayAttendance.checkOut) return 'Present';
    if (todayAttendance.checkIn) return 'Checked In';
    return 'Unknown';
  };

  function getStudentTodayAttendance(studentId) {
    const today = getPakistanDate();
    const attendance = attendanceMap[studentId] || [];
    return attendance.find(a => a.date === today);
  }
  function getStudentAttendanceStatus(studentId) {
    const todayAttendance = getStudentTodayAttendance(studentId);
    if (!todayAttendance) return 'Absent';
    if (todayAttendance.checkIn && todayAttendance.checkOut) return 'Present';
    if (todayAttendance.checkIn) return 'Checked In';
    return 'Unknown';
  }

  const openMessageModal = (recipientId, recipientType) => {
    if (recipientType === 'student') {
      setMessageStudentId(recipientId);
      setMessageEmployeeId(null);
    } else if (recipientType === 'employee') {
      setMessageEmployeeId(recipientId);
      setMessageStudentId(null);
    }
    setMessageRecipientType(recipientType);
    setMessageText('');
    setMessageModalOpen(true);
    setMessageSuccess(false);
  };
  const closeMessageModal = () => {
    setMessageModalOpen(false);
    setMessageStudentId(null);
    setMessageEmployeeId(null);
    setMessageRecipientType(null);
    setMessageText('');
    setMessageSuccess(false);
  };
  const sendMessage = async () => {
    setMessageLoading(true);
    try {
      if (messageRecipientType === 'student') {
        // Find the student by document ID to get their UID
        const studentDocRef = doc(firestore, 'students', messageStudentId);
        const studentDoc = await getDoc(studentDocRef);
        
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          // Use the student's UID to update the document
          const studentUIDRef = doc(firestore, 'students', studentData.uid);
          await updateDoc(studentUIDRef, { message: messageText });
          setMessageSuccess(true);
        } else {
          alert('Student not found.');
        }
      } else if (messageRecipientType === 'employee') {
        // Find the employee by document ID to get their UID
        const employeeDocRef = doc(firestore, 'employees', messageEmployeeId);
        const employeeDoc = await getDoc(employeeDocRef);
        
        if (employeeDoc.exists()) {
          const employeeData = employeeDoc.data();
          // Use the employee's UID to update the document
          const employeeUIDRef = doc(firestore, 'employees', employeeData.uid);
          await updateDoc(employeeUIDRef, { message: messageText });
          setMessageSuccess(true);
        } else {
          alert('Employee not found.');
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message.');
    }
    setMessageLoading(false);
  };

  const openExpenseModal = () => {
    setExpenseDesc('');
    setExpenseAmount('');
    setExpenseError('');
    setExpenseModalOpen(true);
  };
  const closeExpenseModal = () => {
    setExpenseModalOpen(false);
    setExpenseDesc('');
    setExpenseAmount('');
    setExpenseError('');
  };

  const openClientDetailsModal = (client) => {
    setSelectedClient(client);
    setClientDetailsModalOpen(true);
  };

  const closeClientDetailsModal = () => {
    setClientDetailsModalOpen(false);
    setSelectedClient(null);
  };

  const formatExpenseDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const formatExpenseTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString();
    } catch (error) {
      return '';
    }
  };
  const addExpense = async () => {
    if (!expenseDesc.trim() || !expenseAmount || isNaN(expenseAmount) || parseFloat(expenseAmount) <= 0) {
      setExpenseError('Please enter a valid description and amount.');
      return;
    }
    
    try {
      const newExpense = {
        desc: expenseDesc.trim(),
        amount: parseFloat(expenseAmount),
        createdAt: new Date().toISOString()
      };
      
      await addDoc(collection(firestore, 'expenses'), newExpense);
      await fetchExpenses(); // Refresh the expenses list
      closeExpenseModal();
    } catch (error) {
      console.error('Error adding expense:', error);
      setExpenseError('Failed to add expense. Please try again.');
    }
  };

  const totalFees = students.reduce((sum, s) => sum + (parseFloat(s.submittedFee) || 0), 0);
  const totalSalary = employees.reduce((sum, e) => sum + (parseFloat(e.paidSalary) || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const profitOrLoss = totalFees - (totalSalary + totalExpenses);
  const totalPendingFees = students.reduce((sum, s) => sum + (parseFloat(s.pendingFee) || 0), 0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAllData();
    fetchExpenses();
    fetchCourses();
    // Set tab from query param
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'members' || tab === 'students' || tab === 'dashboard' || tab === 'finance' || tab === 'courses') {
      setActiveTab(tab);
    } else {
      setActiveTab('dashboard');
    }
  }, [location.search]);

  const openOutlineModal = (course) => {
    setSelectedCourse(course);
    setShowOutlineModal(true);
    setOutlineInput("");
  };
  const closeOutlineModal = () => {
    setShowOutlineModal(false);
    setSelectedCourse(null);
    setOutlineInput("");
  };

  const addOutline = async () => {
    if (!outlineInput.trim() || !selectedCourse) return;
    setOutlineLoading(true);
    try {
      const newOutlines = [...(selectedCourse.outlines || []), outlineInput.trim()];
      await updateDoc(doc(firestore, 'courses', selectedCourse.id), { outlines: newOutlines });
      setSelectedCourse({ ...selectedCourse, outlines: newOutlines });
      setCourses(prev => prev.map(c => c.id === selectedCourse.id ? { ...c, outlines: newOutlines } : c));
      setOutlineInput("");
    } catch (e) {
      alert('Failed to add outline.');
    }
    setOutlineLoading(false);
  };

  const deleteOutline = async (idx) => {
    if (!selectedCourse) return;
    setOutlineLoading(true);
    try {
      const newOutlines = (selectedCourse.outlines || []).filter((_, i) => i !== idx);
      await updateDoc(doc(firestore, 'courses', selectedCourse.id), { outlines: newOutlines });
      setSelectedCourse({ ...selectedCourse, outlines: newOutlines });
      setCourses(prev => prev.map(c => c.id === selectedCourse.id ? { ...c, outlines: newOutlines } : c));
    } catch (e) {
      alert('Failed to delete outline.');
    }
    setOutlineLoading(false);
  };

  // Handler for checking an outline (admin, local only)
  const handleAdminCheckOutline = (idx) => {
    if (adminCheckedOutlines.includes(idx)) return;
    setAdminCheckedOutlines([...adminCheckedOutlines, idx]);
  };

  const handleDropdownToggle = () => {
    if (!memberDropdownOpen && memberBtnRef.current) {
      setDropdownAnchorRect(memberBtnRef.current.getBoundingClientRect());
    }
    setMemberDropdownOpen(v => !v);
  };
  const handleDropdownClose = () => setMemberDropdownOpen(false);

  



  if (loading) {
    return <LoadingSpinner />;
  }

  // Calculate dashboard summary values
  const totalStudents = students.length;
  const totalMembers = employees.length;
  const totalStudentsPresent = students.filter(s => {
    const att = attendanceMap[s.uid]?.find(a => a.date === today);
    return att && att.checkIn && att.checkOut;
  }).length;
  const totalMembersPresent = employees.filter(e => {
    const att = attendanceMap[e.uid]?.find(a => a.date === today);
    return att && att.checkIn && att.checkOut;
  }).length;

  return (
    <div className="flex flex-row min-h-screen bg-gradient-to-br from-orange-50 via-purple-50 to-orange-50">
      {/* Chat Icon for Admin */}
      {(userRole === 'admin' || userRole === 'manager') && <ChatIcon userRole={userRole} />}
      {/* Mobile Sidebar Toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside className={`hidden lg:block w-72 bg-gradient-to-b from-orange-900 via-purple-800 to-orange-900 shadow-2xl h-full fixed left-0 top-0 z-40`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-purple-700 bg-gradient-to-r from-orange-800 via-purple-800 to-orange-800 relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 via-purple-400/10 to-orange-400/10 animate-pulse"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 via-purple-400 to-orange-400 animate-pulse"></div>
            
            <div className="flex items-center space-x-3 relative z-10 animate-slide-in-left">
              <div className="relative">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center shadow-lg animate-pulse">
                  <img
                    src="/DSH.png"
                    alt="Logo"
                    className="w-10 h-10 object-contain rounded-xl"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
              </div>
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold text-white animate-fade-in-delay drop-shadow-lg">
                  Digital Skills House
                </h1>
                <p className="text-orange-200 text-sm animate-fade-in-delay-2">
                  {userRole === 'admin' ? 'Admin Panel' : 'Manager Panel'}
                </p>
              </div>
            </div>
          </div>
          {/* Navigation */}
          <nav className="flex-1 p-6 space-y-2">
            {/* Manager Dashboard tab at the very top */}
            {userRole === 'manager' && (
            <button
              onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'dashboard' ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white shadow-lg' : 'text-orange-100 hover:bg-gradient-to-r hover:from-orange-400/20 hover:to-purple-400/20'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'dashboard' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M13 5v6h6" />
                </svg>
              </div>
              <span className="font-medium text-base">Dashboard</span>
            </button>
            )}
            {/* Dashboard tab: only for admin */}
            {userRole === 'admin' && (
            <button
              onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'dashboard' ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white shadow-lg' : 'text-orange-100 hover:bg-gradient-to-r hover:from-orange-400/20 hover:to-purple-400/20'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'dashboard' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M13 5v6h6" />
                </svg>
              </div>
              <span className="font-medium text-base">Dashboard</span>
            </button>
            )}
            {/* Members tab: only for admin */}
            {userRole === 'admin' && (
            <button
              onClick={() => { setActiveTab('members'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'members' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'members' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
                <span className="font-medium text-base">Members</span>
            </button>
            )}
            {/* Students tab: admin and manager */}
            {(userRole === 'admin' || userRole === 'manager') && (
            <button
                onClick={() => { setActiveTab('students'); setSidebarOpen(false); }}
                className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'students' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'students' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
                <span className="font-medium text-base">Students</span>
            </button>
            )}
            {/* Finance tab: admin and manager */}
            {(userRole === 'admin' || userRole === 'manager') && (
            <button
              onClick={() => { setActiveTab('finance'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'finance' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'finance' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="font-medium text-base">Finance</span>
            </button>
            )}
            {/* Invoice tab: admin and manager */}
            {(userRole === 'admin' || userRole === 'manager') && (
              <button
                onClick={() => { setActiveTab('invoice'); setSidebarOpen(false); }}
                className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'invoice' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'invoice' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-medium text-base">Invoice</span>
              </button>
            )}
                        {/* Courses tab: only for admin */}
            {userRole === 'admin' && (
            <button
              onClick={() => { setActiveTab('courses'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'courses' ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white shadow-lg' : 'text-orange-100 hover:bg-gradient-to-r hover:from-orange-400/20 hover:to-purple-400/20'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'courses' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
                <span className="font-medium text-base">Courses</span>
            </button>
            )}
            {/* Clients tab: only for admin */}
            {userRole === 'admin' && (
            <button
              onClick={() => { setActiveTab('clients'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-2 py-2 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'clients' ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white shadow-lg' : 'text-orange-100 hover:bg-gradient-to-r hover:from-orange-400/20 hover:to-purple-400/20'}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-2 ${activeTab === 'clients' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
                                        <span className="font-bold text-base">Clients</span>
            </button>
            )}
           
          </nav>
          {/* Footer */}
          <div className="p-6 border-t border-blue-700">
            <button 
              onClick={handleLogout} 
              className="w-full flex items-center justify-center px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </aside>
      {/* Sidebar overlay for mobile */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-orange-900 via-purple-800 to-orange-900 shadow-2xl transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Header, Navigation, Footer (same as above) */}
          <div className="p-6 border-b border-purple-700">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <img
                  src="/DSH.png"
                  alt="Logo"
                  className="w-10 h-10 object-contain rounded-xl"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
                <p className="text-blue-200 text-sm">Management System</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-6 space-y-2">
            <button
              onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'dashboard' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'dashboard' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M13 5v6h6" />
                </svg>
              </div>
              <span className="font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => { setActiveTab('students'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'students' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'students' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="font-medium">Students</span>
            </button>
            <button
              onClick={() => { setActiveTab('members'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'members' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'members' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="font-medium">Employees</span>
            </button>
            <button
              onClick={() => { setActiveTab('finance'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'finance' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'finance' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" />
                </svg>
              </div>
              <span className="font-medium">Finance</span>
            </button>
            <button
              onClick={() => { setActiveTab('courses'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'courses' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'courses' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="font-medium">Courses</span>
            </button>
            <button
              onClick={() => { setActiveTab('invoice'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'invoice' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'invoice' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="font-medium">Invoice</span>
            </button>
            <button
              onClick={() => { setActiveTab('clients'); setSidebarOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 transform hover:scale-105 ${activeTab === 'clients' ? 'bg-white bg-opacity-20 text-white shadow-lg' : 'text-blue-100 hover:bg-white hover:bg-opacity-10'}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${activeTab === 'clients' ? 'bg-white bg-opacity-20' : 'bg-white bg-opacity-10'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
                                      <span className="font-bold">Clients</span>
            </button>
          </nav>
          <div className="p-6 border-t border-blue-700">
            <button 
              onClick={handleLogout} 
              className="w-full flex items-center justify-center px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </aside>
      {/* Main Content */}
      <main className="w-full flex-1 ml-0 lg:ml-72 px-4 lg:px-8 pt-6">
          {/* Beautiful Header with Tab Name and Quick Add Buttons */}
          {activeTab !== 'invoice' && (
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="relative bg-gradient-to-r from-orange-600 via-purple-600 to-orange-800 rounded-3xl shadow-2xl overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full transform translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full transform -translate-x-12 translate-y-12"></div>
              
              <div className="relative p-6">
                {/* Header Content */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Tab Title Section */}
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                      {activeTab === 'dashboard' && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M13 5v6h6" />
                        </svg>
                      )}
                      {activeTab === 'students' && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      )}
                      {activeTab === 'members' && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      )}
                      {activeTab === 'finance' && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" />
                        </svg>
                      )}
                      {activeTab === 'courses' && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      )}
                      {activeTab === 'clients' && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                        {activeTab === 'dashboard' && 'Dashboard Overview'}
                        {activeTab === 'students' && 'Student Management'}
                        {activeTab === 'members' && 'Employee Management'}
                        {activeTab === 'finance' && 'Financial Overview'}
                        {activeTab === 'courses' && 'Course Management'}
                        {activeTab === 'clients' && 'Client Management'}
                      </h1>
                      <p className="text-blue-100 text-sm">
                        {activeTab === 'dashboard' && 'Complete overview of your institute'}
                        {activeTab === 'students' && 'Manage all student records and information'}
                        {activeTab === 'members' && 'Handle employee data and attendance'}
                        {activeTab === 'finance' && 'Track income, expenses and financial status'}
                        {activeTab === 'courses' && 'Manage and add new courses'}
                        {activeTab === 'clients' && 'Manage client projects and assignments'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Quick Add Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link 
                      to="/add-student" 
                      className="group relative overflow-hidden bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-xl p-3 hover:bg-white/30 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                      <div className="absolute top-0 right-0 w-10 h-10 bg-white/10 rounded-full transform translate-x-5 -translate-y-5 group-hover:scale-150 transition-transform duration-300"></div>
                      <div className="relative z-10 flex items-center space-x-2">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-sm font-bold">Add Student</h3>
                          <p className="text-orange-100 text-xs">Register new student</p>
                        </div>
                      </div>
                    </Link>
                    {/* Add Member Button and Dropdown - show for admin and manager */}
                    {userRole === 'admin' && (
                    <div className="relative" style={{ minWidth: 160 }}>
                      <button
                        ref={memberBtnRef}
                        type="button"
                        className="group relative overflow-hidden bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-xl p-3 hover:bg-white/30 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2 w-full"
                        onClick={handleDropdownToggle}
                      >
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-sm font-bold">Add Member</h3>
                          <p className="text-orange-100 text-xs">Choose type</p>
                        </div>
                        <svg className="w-4 h-4 ml-2 text-white/70 group-hover:text-white transition" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      <MemberDropdownPortal open={memberDropdownOpen} anchorRect={dropdownAnchorRect} onClose={handleDropdownClose} />
                    </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
          {/* Dashboard/Home Section */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Stats Cards */}
              <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
                <div className="bg-blue-100 border border-blue-200 text-blue-800 rounded-xl p-4 text-center shadow transition-transform duration-200 hover:scale-105 hover:shadow-lg cursor-pointer">
                  <div className="text-2xl font-bold">{totalStudents}</div>
                  <div className="font-medium">Total Students</div>
                      </div>
                <div className="bg-green-100 border border-green-200 text-green-800 rounded-xl p-4 text-center shadow transition-transform duration-200 hover:scale-105 hover:shadow-lg cursor-pointer">
                  <div className="text-2xl font-bold">{totalMembers}</div>
                  <div className="font-medium">Total Members</div>
                    </div>
                <div className="bg-purple-100 border border-purple-200 text-purple-800 rounded-xl p-4 text-center shadow transition-transform duration-200 hover:scale-105 hover:shadow-lg cursor-pointer">
                  <div className="text-2xl font-bold">{totalStudentsPresent}</div>
                  <div className="font-medium">Total Students Present</div>
                  </div>
                <div className="bg-yellow-100 border border-yellow-200 text-yellow-800 rounded-xl p-4 text-center shadow transition-transform duration-200 hover:scale-105 hover:shadow-lg cursor-pointer">
                  <div className="text-2xl font-bold">{totalMembersPresent}</div>
                  <div className="font-medium">Total Members Present</div>
                </div>
              </div>

              {/* Overview Card */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Institute Overview</h2>
                    <p className="text-gray-600">Complete management dashboard</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Quick Actions</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Add new students and employees
                      </li>
                      <li className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Monitor attendance and fees
                      </li>
                      <li className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        View detailed reports
                      </li>
                    </ul>
                    
                    {/* Password Migration Section */}
                    {migrationStatus === 'needed' && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="text-sm font-medium text-yellow-800">Password Security Update Required</span>
                          </div>
                          <button
                            onClick={handlePasswordMigration}
                            disabled={migrationLoading}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {migrationLoading ? 'Migrating...' : 'Update Now'}
                          </button>
                        </div>
                        <p className="text-xs text-yellow-700 mt-1">
                          Existing passwords need to be encrypted for enhanced security.
                        </p>
                      </div>
                    )}
                    
                    {migrationStatus === 'completed' && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-green-800">Passwords Secured</span>
                        </div>
                        <p className="text-xs text-green-700 mt-1">
                          All passwords have been encrypted for enhanced security.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Today's Summary</h3>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Attendance Rate</span>
                        <span className="font-semibold text-gray-800">
                          {employees.length > 0 ? Math.round((employees.filter(emp => getAttendanceStatus(emp.uid) === 'Present').length / employees.length) * 100) : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${employees.length > 0 ? (employees.filter(emp => getAttendanceStatus(emp.uid) === 'Present').length / employees.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Employees Tab */}
          {activeTab === 'members' && userRole === 'admin' && (
            <>
              <div className="flex gap-2 mb-4">
                <button
                  className={`px-4 py-2 rounded-lg font-semibold border transition-all ${employeeTab === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                  onClick={() => setEmployeeTab('all')}
                >
                  All Employees
                </button>
                <button
                  className={`px-4 py-2 rounded-lg font-semibold border transition-all ${employeeTab === 'internee' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                  onClick={() => setEmployeeTab('internee')}
                >
                  Internees
                </button>
                <button
                  className={`px-4 py-2 rounded-lg font-semibold border transition-all ${employeeTab === 'manager' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                  onClick={() => setEmployeeTab('manager')}
                >
                  Managers
                </button>
              </div>
              
              {/* Employee Filters */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full mb-4">
                <label className="text-sm font-medium text-gray-700">Attendance:</label>
                <select value={employeeAttendanceFilter} onChange={e => setEmployeeAttendanceFilter(e.target.value)} className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 w-full sm:w-auto">
                  <option value="all">All</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="checkedin">Checked In</option>
                </select>
                <label className="text-sm font-medium text-gray-700">Salary:</label>
                <select value={employeeSalaryFilter} onChange={e => setEmployeeSalaryFilter(e.target.value)} className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 w-full sm:w-auto">
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
                <input type="text" placeholder="Search by name..." value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 w-full sm:w-auto" />
              </div>
              
              {/* Pagination Info for Members */}
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-gray-600">
                  {showAllMembers 
                    ? `Showing all ${employees.length} members`
                    : `Showing ${Math.min((currentMemberPage - 1) * membersPerPage + 1, employees.length)} to ${Math.min(currentMemberPage * membersPerPage, employees.length)} of ${employees.length} members`
                  }
                </div>
                <div className="flex gap-2">
                  {!showAllMembers && (
                    <>
                      <button
                        onClick={() => setCurrentMemberPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentMemberPage === 1}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm bg-gray-100 rounded">
                        Page {currentMemberPage} of {Math.ceil(employees.length / membersPerPage)}
                      </span>
                      <button
                        onClick={() => setCurrentMemberPage(prev => Math.min(prev + 1, Math.ceil(employees.length / membersPerPage)))}
                        disabled={currentMemberPage >= Math.ceil(employees.length / membersPerPage)}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                      >
                        Next
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowAllMembers(!showAllMembers)}
                    className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    {showAllMembers ? 'Show Paginated' : 'Show All'}
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {employees
                  .filter(emp => {
                    if (employeeTab === 'all') return true;
                    if (employeeTab === 'internee') return (emp.position || '').toLowerCase() === 'internee';
                    if (employeeTab === 'manager') return (emp.position || '').toLowerCase() === 'manager';
                    return true;
                  })
                  .filter(emp => {
                    if (employeeAttendanceFilter === 'all') return true;
                    const status = getAttendanceStatus(emp.uid);
                    if (employeeAttendanceFilter === 'present' && status !== 'Present') return false;
                    if (employeeAttendanceFilter === 'absent' && status !== 'Absent') return false;
                    if (employeeAttendanceFilter === 'checkedin' && status !== 'Checked In') return false;
                    return true;
                  })
                  .filter(emp => {
                    if (employeeSalaryFilter === 'all') return true;
                    const salary = parseFloat(emp.salary) || 0;
                    const paidSalary = parseFloat(emp.paidSalary) || 0;
                    const remainingSalary = salary - paidSalary;
                    if (employeeSalaryFilter === 'paid' && remainingSalary === 0) return true;
                    if (employeeSalaryFilter === 'pending' && remainingSalary > 0) return true;
                    return false;
                  })
                  .filter(emp => emp.fullName && emp.fullName.toLowerCase().includes(employeeSearch.toLowerCase()))
                  .slice(showAllMembers ? 0 : (currentMemberPage - 1) * membersPerPage, showAllMembers ? undefined : currentMemberPage * membersPerPage)
                  .map(emp => {
                    const attendanceStatus = getAttendanceStatus(emp.uid);
                    const todayAttendance = getTodayAttendance(emp.uid);
                    return (
                      <div key={emp.id} className={`group relative bg-gradient-to-br from-white via-green-50 to-green-100 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] border-l-8 ${emp.disabled ? 'border-red-400 opacity-75' : 'border-green-400'}`}>
                        {emp.disabled && (
                          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold z-10">
                            DISABLED
                          </div>
                        )}
                        <div className="p-6">
                          {/* Employee Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-4 border-white flex-shrink-0">
                                <span className="text-xl font-bold text-white drop-shadow-lg">
                                  {emp.fullName ? emp.fullName.charAt(0).toUpperCase() : 'E'}
                                </span>
                              </div>
                              <div className="ml-3 flex-1 min-w-0">
                                <h3 className="text-lg font-bold text-gray-900 tracking-tight truncate">
                                  {emp.fullName}
                                </h3>
                                <div className="mt-1">
                                  <span className="inline-block bg-green-200 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">{emp.position || 'Member'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex space-x-2 ml-2 flex-shrink-0">
                              <button 
                                onClick={() => {
                                  setEmployeeToDelete(emp.id);
                                  setShowDeleteEmployeeModal(true);
                                }}
                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                title="Delete Member"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          {/* Employee Details */}
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              {emp.contactNumber}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              <span className="text-gray-700 font-mono text-xs">
                                {emp.adminPassword ? decryptForAdmin(emp.adminPassword) : emp.password}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              <span className="font-medium text-gray-700">Salary:</span> {emp.salary}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                                                             <span className="font-medium text-gray-700">Joining:</span> {emp.dateJoining || emp.joiningDate || '-'}
                            </div>
                          </div>
                          {/* Attendance Status */}
                          <div className="mb-4">
                            <div className="flex items-center justify-evenly gap-2">
                              <span className="text-sm font-medium text-gray-700">Today's Status:</span>
                              <span className={`px-1 py-1 rounded-full text-xs font-semibold shadow-sm border border-gray-200 ${
                                attendanceStatus === 'Present' ? 'bg-green-100 text-green-800' :
                                attendanceStatus === 'Checked In' ? 'bg-yellow-100 text-yellow-800' :
                                attendanceStatus === 'Absent' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {attendanceStatus}
                              </span>
                            </div>
                          </div>
                          {/* Today's Attendance Details */}
                          {todayAttendance && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Today's Attendance</h4>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-600">Check-in:</span>
                                  <span className="ml-1 font-medium">{todayAttendance.checkIn || '—'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Check-out:</span>
                                  <span className="ml-1 font-medium">{todayAttendance.checkOut || '—'}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Action Buttons */}
                          <div className="flex space-x-1">
                            <button 
                              onClick={() => navigate(`/employee-details/${emp.id}?tab=members`)}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                            >
                              View Details
                            </button>
                            <button 
                              onClick={() => navigate(`/edit-employee/${emp.id}?tab=members`)}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-gray-500 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                            >
                              Edit
                            </button>
                            <button
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                              onClick={() => openMessageModal(emp.id, 'employee')}
                            >
                              Message
                            </button>
                            {emp.disabled ? (
                              <button
                                onClick={() => handleEnableEmployee(emp)}
                                className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors duration-200"
                                title="Enable Employee"
                              >
                                Enable
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setEmployeeToDisable(emp);
                                  setShowDisableEmployeeModal(true);
                                }}
                                className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors duration-200"
                                title="Disable Employee"
                              >
                                Disable
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full mb-4">
                <label className="text-sm font-medium text-gray-700">Fee Status:</label>
                <select value={studentFeeFilter} onChange={e => setStudentFeeFilter(e.target.value)} className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 w-full sm:w-auto">
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
                <label className="text-sm font-medium text-gray-700">Attendance:</label>
                <select value={studentAttendanceFilter} onChange={e => setStudentAttendanceFilter(e.target.value)} className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 w-full sm:w-auto">
                  <option value="all">All</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="checkedin">Checked In</option>
                </select>
                <input type="text" placeholder="Search by name..." value={studentSearch} onChange={e => setStudentSearch(e.target.value)} className="rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 w-full sm:w-auto" />
              </div>
              {/* Pagination Info */}
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-gray-600">
                  {showAllStudents 
                    ? `Showing all ${students.length} students`
                    : `Showing ${Math.min((currentStudentPage - 1) * studentsPerPage + 1, students.length)} to ${Math.min(currentStudentPage * studentsPerPage, students.length)} of ${students.length} students`
                  }
                </div>
                <div className="flex gap-2">
                  {!showAllStudents && (
                    <>
                      <button
                        onClick={() => setCurrentStudentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentStudentPage === 1}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm bg-gray-100 rounded">
                        Page {currentStudentPage} of {Math.ceil(students.length / studentsPerPage)}
                      </span>
                      <button
                        onClick={() => setCurrentStudentPage(prev => Math.min(prev + 1, Math.ceil(students.length / studentsPerPage)))}
                        disabled={currentStudentPage >= Math.ceil(students.length / studentsPerPage)}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600"
                      >
                        Next
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowAllStudents(!showAllStudents)}
                    className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    {showAllStudents ? 'Show Paginated' : 'Show All'}
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {students
                  .filter(student => {
                    if (studentFeeFilter === 'all') return true;
                    if (studentFeeFilter === 'paid') return parseFloat(student.pendingFee) === 0;
                    if (studentFeeFilter === 'pending') return parseFloat(student.pendingFee) > 0;
                    return true;
                  })
                  .filter(student => {
                    if (studentAttendanceFilter === 'all') return true;
                    const status = getStudentAttendanceStatus(student.uid);
                    if (studentAttendanceFilter === 'present' && status !== 'Present') return false;
                    if (studentAttendanceFilter === 'absent' && status !== 'Absent') return false;
                    if (studentAttendanceFilter === 'checkedin' && status !== 'Checked In') return false;
                    return true;
                  })
                  .filter(student => student.fullName && student.fullName.toLowerCase().includes(studentSearch.toLowerCase()))
                  .slice(showAllStudents ? 0 : (currentStudentPage - 1) * studentsPerPage, showAllStudents ? undefined : currentStudentPage * studentsPerPage)
                  .map(student => (
                    <div key={student.id} className={`group relative bg-gradient-to-br from-white via-blue-50 to-blue-100 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] border-l-8 ${student.disabled ? 'border-red-400 opacity-75' : 'border-blue-400'}`}>
                      {student.disabled && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold z-10">
                          DISABLED
                        </div>
                      )}
                      <div className="p-6">
                        {/* Student Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-400 via-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white flex-shrink-0">
                              <span className="text-xl font-bold text-white drop-shadow-lg">
                                {student.fullName ? student.fullName.charAt(0).toUpperCase() : 'S'}
                              </span>
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                              <h3 className="text-lg font-bold text-gray-900 tracking-tight truncate">
                                {student.fullName}
                              </h3>
                              <div className="mt-1">
                                <span className="inline-block bg-blue-200 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">{student.courseName}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2 ml-2 flex-shrink-0">
                            <button 
                              onClick={() => {
                                setStudentToDelete(student.id);
                                setShowDeleteStudentModal(true);
                              }}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Delete Student"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Student Details */}
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {student.contactNumber}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span className="text-gray-700 font-mono text-xs">
                              {student.adminPassword ? decryptForAdmin(student.adminPassword) : student.password}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="font-medium text-gray-700">Admission:</span> {student.admissionDate}
                          </div>
                        </div>

                        {/* Fee Status */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Fee Status:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm border border-gray-200 ${
                              parseFloat(student.pendingFee) === 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {parseFloat(student.pendingFee) === 0 ? 'Paid' : 'Pending'}
                            </span>
                          </div>
                        </div>

                        {/* Fee Details */}
                        <div className="bg-white/80 rounded-lg p-3 mb-4 border border-blue-100">
                          <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-1">
                            <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            Fee Details
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-600">Total:</span>
                              <span className="ml-1 font-semibold text-blue-700">PKR {student.totalFee}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Paid:</span>
                              <span className="ml-1 font-semibold text-green-700">PKR {student.submittedFee}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-600">Pending:</span>
                              <span className="ml-1 font-semibold text-red-600">PKR {student.pendingFee}</span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-1">
                          <button 
                            onClick={() => navigate(`/student-details/${student.id}?tab=students`)}
                            className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                          >
                            View Details
                          </button>
                          <button 
                            onClick={() => navigate(`/edit-student/${student.id}?tab=students`)}
                            className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-gray-500 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                          >
                            Edit
                          </button>
                          <button
                            className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                            onClick={() => openMessageModal(student.id, 'student')}
                          >
                            Message
                          </button>
                          {student.disabled ? (
                            <button
                              onClick={() => handleEnableStudent(student)}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors duration-200"
                              title="Enable Student"
                            >
                              Enable
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setStudentToDisable(student);
                                setShowDisableStudentModal(true);
                              }}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors duration-200"
                              title="Disable Student"
                            >
                              Disable
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mb-4">
                        <div className="flex items-center justify-evenly gap-2">
                          <span className="text-sm font-medium text-gray-700">Today's Status:</span>
                          <span className={`px-1 py-1 rounded-full text-xs font-semibold shadow-sm border border-gray-200 ${
                            getStudentAttendanceStatus(student.uid) === 'Present' ? 'bg-green-100 text-green-800' :
                            getStudentAttendanceStatus(student.uid) === 'Checked In' ? 'bg-yellow-100 text-yellow-800' :
                            getStudentAttendanceStatus(student.uid) === 'Absent' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getStudentAttendanceStatus(student.uid)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Finance Tab */}
          {activeTab === 'finance' && (
            userRole === 'admin' ? (
              <>
                {/* Finance Cards and Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-green-400 via-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold">Total Fees Collected</span>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" /></svg>
              </div>
                    <div className="text-2xl font-bold">Rs. {totalFees}</div>
                      </div>
                  <div className="bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 rounded-2xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold">Total Salary Paid</span>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" /></svg>
                    </div>
                    <div className="text-2xl font-bold">Rs. {totalSalary}</div>
                  </div>
                  <div className="bg-gradient-to-br from-red-400 via-red-500 to-red-600 rounded-2xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold">Total Expenses</span>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" /></svg>
                </div>
                    <div className="text-2xl font-bold">Rs. {totalExpenses}</div>
                      </div>
                    </div>
                {/* Profit/Loss and Pending Fees */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-2xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold">Profit / Loss</span>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" /></svg>
                  </div>
                    <div className="text-2xl font-bold">Rs. {profitOrLoss}</div>
                </div>
                  <div className="bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold">Total Pending Fees</span>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-4.418 0-8 1.79-8 4v4a2 2 0 002 2h12a2 2 0 002-2v-4c0-2.21-3.582-4-8-4zm0 0V4m0 0a4 4 0 110 8 4 4 0 010-8z" /></svg>
                      </div>
                    <div className="text-2xl font-bold">Rs. {totalPendingFees}</div>
                    </div>
                  </div>
                {/* Project Revenue and Profit/Loss Summary */}
                <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Project Financial Summary</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-indigo-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-600">Total Project Revenue</span>
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div className="text-xl font-bold text-green-600">
                        Rs. {clients.reduce((sum, client) => sum + (parseFloat(client.projectCost) || 0), 0).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-indigo-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-600">Total Developer Payments</span>
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div className="text-xl font-bold text-red-600">
                        Rs. {clients.reduce((sum, client) => sum + (parseFloat(client.developerPay) || 0), 0).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-indigo-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-600">Net Profit/Loss</span>
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className={`text-xl font-bold ${(() => {
                        const totalRevenue = clients.reduce((sum, client) => sum + (parseFloat(client.projectCost) || 0), 0);
                        const totalPayments = clients.reduce((sum, client) => sum + (parseFloat(client.developerPay) || 0), 0);
                        const netProfit = totalRevenue - totalPayments;
                        return netProfit >= 0 ? 'text-green-600' : 'text-red-600';
                                              })()}`}>
                        Rs. {(() => {
                          const totalRevenue = clients.reduce((sum, client) => sum + (parseFloat(client.projectCost) || 0), 0);
                          const totalPayments = clients.reduce((sum, client) => sum + (parseFloat(client.developerPay) || 0), 0);
                          return (totalRevenue - totalPayments).toLocaleString();
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Add Expense Button and Modal */}
                <div className="mb-8">
                  {expenseModalOpen ? (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Add Expense</h2>
                        {expenseError && <div className="text-red-500 mb-2">{expenseError}</div>}
                        <input
                          type="text"
                          placeholder="Expense Description"
                          value={expenseDesc}
                          onChange={e => setExpenseDesc(e.target.value)}
                          className="w-full p-3 mb-4 border rounded"
                        />
                        <input
                          type="number"
                          placeholder="Amount"
                          value={expenseAmount}
                          onChange={e => setExpenseAmount(e.target.value)}
                          className="w-full p-3 mb-4 border rounded"
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={closeExpenseModal} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                          <button onClick={addExpense} className="px-4 py-2 bg-blue-600 text-white rounded">Add</button>
                </div>
                      </div>
                    </div>
                  ) : (
                    <button onClick={openExpenseModal} className="py-2 px-4 bg-blue-600 text-white rounded">Add Expense</button>
                  )}
                  </div>
                {/* Expense History for admin */}
                {expenses.length === 0 ? (
                  <div className="mt-8 text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses found</h3>
                    <p className="text-sm text-gray-500">Start tracking your expenses by adding the first one.</p>
                  </div>
                ) : (
                  <div className="mt-8">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-800">Expense History</h3>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Search expenses by description..."
                          value={expenseSearchTerm}
                          onChange={(e) => setExpenseSearchTerm(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                          <div className="col-span-6">Description</div>
                          <div className="col-span-2 text-center">Amount</div>
                          <div className="col-span-3 text-center">Date</div>
                          <div className="col-span-1 text-center">Action</div>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {expenses
                          .filter(exp => 
                            exp.desc.toLowerCase().includes(expenseSearchTerm.toLowerCase()) ||
                            exp.amount.toString().includes(expenseSearchTerm)
                          )
                          .map(exp => (
                          <div key={exp.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                            <div className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-6">
                                <span className="text-gray-900 font-medium">{exp.desc}</span>
                              </div>
                              <div className="col-span-2 text-center">
                                <span className="font-semibold text-red-600">Rs. {exp.amount}</span>
                              </div>
                              <div className="col-span-3 text-center">
                                <span className="text-sm text-gray-500">{formatExpenseDate(exp.createdAt)}</span>
                                <div className="text-xs text-gray-400">{formatExpenseTime(exp.createdAt)}</div>
                              </div>
                              <div className="col-span-1 text-center">
                                <button 
                                  onClick={() => handleDeleteExpense(exp.id)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Expense"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 flex flex-col items-center justify-center">
                {expenseModalOpen ? (
                  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                    <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
                      <h2 className="text-xl font-bold mb-4">Add Expense</h2>
                      {expenseError && <div className="text-red-500 mb-2">{expenseError}</div>}
                    <input
                      type="text"
                      placeholder="Expense Description"
                      value={expenseDesc}
                      onChange={e => setExpenseDesc(e.target.value)}
                        className="w-full p-3 mb-4 border rounded"
                    />
                    <input
                      type="number"
                        placeholder="Amount"
                      value={expenseAmount}
                      onChange={e => setExpenseAmount(e.target.value)}
                        className="w-full p-3 mb-4 border rounded"
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={closeExpenseModal} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                        <button onClick={addExpense} className="px-4 py-2 bg-blue-600 text-white rounded">Add</button>
                    </div>
                  </div>
                </div>
                ) : (
                  <button onClick={openExpenseModal} className="py-2 px-4 bg-blue-600 text-white rounded">Add Expense</button>
                )}
                                {/* Expense History for manager */}
                {expenses.length === 0 ? (
                  <div className="mt-8 text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses found</h3>
                    <p className="text-sm text-gray-500">Start tracking your expenses by adding the first one.</p>
                  </div>
                ) : (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-6 text-gray-800">Expense History</h3>
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                          <div className="col-span-6">Description</div>
                          <div className="col-span-2 text-center">Amount</div>
                          <div className="col-span-3 text-center">Date</div>
                          <div className="col-span-1 text-center">Action</div>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {expenses.map(exp => (
                          <div key={exp.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                            <div className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-6">
                                <span className="text-gray-900 font-medium">{exp.desc}</span>
                              </div>
                              <div className="col-span-2 text-center">
                                <span className="font-semibold text-red-600">Rs. {exp.amount}</span>
                              </div>
                              <div className="col-span-3 text-center">
                                <span className="text-sm text-gray-500">{formatExpenseDate(exp.createdAt)}</span>
                                <div className="text-xs text-gray-400">{formatExpenseTime(exp.createdAt)}</div>
                              </div>
                              <div className="col-span-1 text-center">
                                <button 
                                  onClick={() => handleDeleteExpense(exp.id)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Expense"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {/* Empty States */}
          {activeTab === 'members' && employees.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No members</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first institute member.</p>
              <div className="mt-6">
                <Link to="/add-employee" className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Member
                </Link>
              </div>
            </div>
          )}

          {activeTab === 'students' && students.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No students</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first student.</p>
              <div className="mt-6">
                <Link to="/add-student" className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Student
                </Link>
              </div>
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-800">Course Management</h2>
                </div>
                <button
                  onClick={() => setCourseModalOpen(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Course
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {courses.map(course => (
                  <div key={course.id} className="relative flex flex-col justify-between h-56 bg-white rounded-xl shadow p-4">
                    {/* Top: Icon and Delete Button */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCourse(course.id);
                        }}
                        className="p-1 text-red-400 hover:text-red-600 bg-white rounded-full shadow-sm z-10"
                        title="Delete Course"
                        style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    {/* Course Title */}
                    <p
                      className="text-sm font-normal text-gray-800 tracking-wide my-1"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {course.name}
                    </p>
                    {/* Bottom: Students, Outlines, and Quiz Buttons */}
                    <div className="space-y-2">
                      {/* Students Count */}
                      <div className="bg-purple-100 rounded-lg flex items-center justify-between px-3 py-2">
                        <span className="text-sm text-purple-700 font-medium">Students:</span>
                        <span className="bg-purple-500 text-white rounded-full px-3 py-1 font-bold">
                          {students.filter(student => student.courseName === course.name).length}
                        </span>
                      </div>
                      {/* Buttons Row */}
                      <div className="flex gap-1">
                        {/* Outlines Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openOutlineModal(course);
                          }}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium py-2 px-2 rounded-lg transition-colors flex items-center justify-center gap-1"
                          title="View Course Outlines"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Outlines
                        </button>
                        {/* Quiz Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuizCourse(course);
                            setQuizModalOpen(true);
                          }}
                          className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium py-2 px-2 rounded-lg transition-colors flex items-center justify-center gap-1"
                          title="Manage Quiz Questions"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Quiz
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {courses.length === 0 && (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No courses</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by adding your first course.</p>
                  <div className="mt-6">
                    <button
                      onClick={() => setCourseModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Course
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invoice Tab */}
          {activeTab === 'invoice' && (
            <Invoice />
          )}
          
          {/* Clients Tab */}
          {activeTab === 'clients' && userRole === 'admin' && (
            <div className="space-y-6">
              {/* Search and Filter */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Search clients..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                  <button
                    onClick={() => openClientModal()}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Client
                  </button>
                </div>
              </div>

              {/* Clients Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {clients
                  .filter(client => 
                    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                    client.projectName?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                    client.assignedTo?.toLowerCase().includes(clientSearch.toLowerCase())
                  )
                  .map(client => (
                    <div key={client.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openClientDetailsModal(client)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              title="View Details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => openClientModal(client)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                              title="Edit Client"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => deleteClient(client.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                              title="Delete Client"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <h3 className="text-lg font-bold text-gray-800">{client.name}</h3>
                          </div>
                          
                          <div className="space-y-2">
                            
                            {client.projectName && (
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-gray-700 font-bold">{client.projectName}</span>
                              </div>
                            )}
                            
                            {client.assignedTo && (
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className="text-gray-700"><span className="font-bold">Assigned to:</span> {client.assignedTo}</span>
                              </div>
                            )}
                            
                            {client.projectCost && (
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                                <span className="text-gray-700"><span className="font-bold">Cost:</span> Rs. {client.projectCost.toLocaleString()}</span>
                              </div>
                            )}
                            
                            {client.developerPay && (
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                                <span className="text-gray-700"><span className="font-bold">Dev Pay:</span> Rs. {client.developerPay.toLocaleString()}</span>
                              </div>
                            )}
                            

                            
                            {client.deadline && (
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-gray-700"><span className="font-bold">Deadline:</span> {new Date(client.deadline).toLocaleDateString()}</span>
                              </div>
                            )}
                            
                            {client.projectReceiveDate && (
                              <div className="flex items-center text-sm">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-gray-700"><span className="font-bold">Received:</span> {new Date(client.projectReceiveDate).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <span className="text-xs text-gray-500">
                              {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Empty State */}
              {clients.filter(client => 
                client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                client.projectName?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                client.assignedTo?.toLowerCase().includes(clientSearch.toLowerCase())
              ).length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No clients found</h3>
                  <p className="text-gray-500 mb-4">
                    {clientSearch ? 'No clients match your search criteria.' : 'Get started by adding your first client.'}
                  </p>
                  {!clientSearch && (
                    <button
                      onClick={() => openClientModal()}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                    >
                      Add Your First Client
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Main content: Manager dashboard portal */}
          {activeTab === 'dashboard' && userRole === 'manager' && (
            (() => {
              if (!currentUser || !employees || employees.length === 0) {
                return <div className="text-center p-8">Loading...</div>;
              }
              const managerData = employees.find(e => e.uid === currentUser.uid) || {};
              const attendanceList = attendanceMap[currentUser.uid] || [];
              const presentCount = attendanceList.filter(a => a.checkIn && a.checkOut).length;
              const checkedInCount = attendanceList.filter(a => a.checkIn && !a.checkOut).length;
              const absentCount = attendanceList.length - presentCount - checkedInCount;
              const today = getPakistanDate();
              const todayAttendance = attendanceList.find(a => a.date === today);
              // Check In/Out handlers
              const handleCheckIn = async () => {
                try {
                  const attendanceRef = collection(firestore, 'employeeAttendance');
                  const docId = `${currentUser.uid}_${today}`;
                  await setDoc(doc(attendanceRef, docId), {
                    uid: currentUser.uid,
                    date: today,
                    checkIn: getPakistanTime(),
                    userType: 'employee',
                  }, { merge: true });
                  showSuccess('Checked in successfully!');
                  fetchAllData();
                } catch (err) {
                  showError('Failed to check in.');
                }
              };
              const handleCheckOut = async () => {
                try {
                  const attendanceRef = collection(firestore, 'employeeAttendance');
                  const docId = `${currentUser.uid}_${today}`;
                  const docRef = doc(attendanceRef, docId);
                  const snap = await getDoc(docRef);
                  if (snap.exists()) {
                    const prev = snap.data();
                    const checkOutTime = getPakistanTime();
                    
                    // Calculate duration
                    let duration = '';
                    if (prev.checkIn) {
                      duration = calculateDuration(prev.checkIn, checkOutTime);
                    }
                    
                    await setDoc(docRef, {
                      uid: currentUser.uid,
                      date: today,
                      checkIn: prev.checkIn || null,
                      checkOut: checkOutTime,
                      duration: duration,
                      userType: 'employee',
                    }, { merge: true });
                    showSuccess('Checked out successfully!');
                    fetchAllData();
                  } else {
                    showError('Check-in record not found for today.');
                  }
                } catch (err) {
                  showError('Failed to check out.');
                }
              };

              // Helper function to calculate duration between two 12-hour time strings

              return (
                <div className="max-w-xl mx-auto p-6 bg-white rounded-2xl shadow-2xl mt-10 flex flex-col items-center">
                  {/* Dashboard summary cards at the top */}
                
                  {/* Attendance Status */}
                  {todayAttendance && todayAttendance.checkIn && todayAttendance.checkOut && (
                    <div className="mb-4 text-green-600 font-semibold text-center">Attendance completed for today.</div>
                  )}
                  {/* Profile Info */}
                  <div className="mb-8 w-full flex flex-col items-center">
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">Profile</h3>
                    <div className="bg-gray-50 rounded-lg shadow p-4 w-full flex flex-col items-center">
                      <div className="text-xl font-bold text-gray-800 mb-1">{managerData.fullName || currentUser?.displayName || '-'}</div>
                      <div className="text-gray-600">Contact: {managerData.contactNumber || '-'}</div>
                    </div>
                  </div>
                  {/* Attendance Summary */}
                  {/* <div className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                    <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-center shadow">
                      <div className="text-2xl font-bold">{presentCount}</div>
                      <div className="font-medium">Total Present</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl p-4 text-center shadow">
                      <div className="text-2xl font-bold">{checkedInCount}</div>
                      <div className="font-medium">Checked In</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-center shadow">
                      <div className="text-2xl font-bold">{absentCount}</div>
                      <div className="font-medium">Total Absent</div>
                    </div>
                  </div> */}
                  {/* Check In/Out Buttons */}
                  <div className="mb-8 flex gap-4">
                    {!todayAttendance && (
                      <button onClick={handleCheckIn} className="px-6 py-2 bg-green-600 text-white rounded shadow hover:bg-green-700 transition">Check In</button>
                    )}
                    {todayAttendance && todayAttendance.checkIn && !todayAttendance.checkOut && (
                      <button onClick={handleCheckOut} className="px-6 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition">Check Out</button>
                    )}
                  </div>
                  {/* Attendance History */}
                  <div className="mb-8 w-full">
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">Your Attendance</h3>
                    {attendanceList.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg shadow">
                          <thead>
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase">Check-in</th>
                              <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase">Check-out</th>
                              <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 uppercase">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attendanceList.map((att, idx) => {
                              let duration = '--';
                              function parseTime(timeStr) {
                                if (!timeStr) return null;
                                // Try 24-hour format first
                                let date = new Date(`1970-01-01T${timeStr}`);
                                if (!isNaN(date.getTime())) return date;
                                // Try 12-hour format (with AM/PM)
                                const match = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2}) ?([APap][Mm])?/);
                                if (match) {
                                  let [, h, m, s, ampm] = match;
                                  h = parseInt(h, 10);
                                  m = parseInt(m, 10);
                                  s = parseInt(s, 10);
                                  if (ampm) {
                                    if (ampm.toLowerCase() === 'pm' && h !== 12) h += 12;
                                    if (ampm.toLowerCase() === 'am' && h === 12) h = 0;
                                  }
                                  return new Date(1970, 0, 1, h, m, s);
                                }
                                return null;
                              }
                              if (att.checkIn && att.checkOut) {
                                const inDate = parseTime(att.checkIn);
                                const outDate = parseTime(att.checkOut);
                                if (inDate && outDate) {
                                  if (outDate < inDate) outDate.setDate(outDate.getDate() + 1); // handle overnight
                                  const diffMs = outDate - inDate;
                                  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
                                  const diffM = Math.floor((diffMs / (1000 * 60)) % 60);
                                  if (!isNaN(diffH) && !isNaN(diffM)) {
                                    duration = `${diffH}h ${diffM}m`;
                                  }
                                }
                              }
                              return (
                                <tr key={idx} className="border-b last:border-none hover:bg-gray-50 transition">
                                  <td className="px-4 py-2 font-medium text-gray-700">{att.date}</td>
                                  <td className="px-4 py-2 text-gray-600">{att.checkIn || '--'}</td>
                                  <td className="px-4 py-2 text-gray-600">{att.checkOut || '--'}</td>
                                  <td className="px-4 py-2 text-blue-700 font-semibold">{duration}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-gray-500">No attendance records found.</div>
                    )}
                  </div>
                  {/* Admin Messages */}
                  <div className="w-full">
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">Admin Messages</h3>
                    {managerData.message ? (
                      <div className="bg-blue-100 text-blue-800 rounded p-4 mb-2 shadow">
                        {managerData.message}
                      </div>
                    ) : (
                      <div className="text-gray-500">No messages from admin.</div>
                    )}
                  </div>
                </div>
              );
            })()
          )}
        </main>
        
        {/* Course Modal */}
        {courseModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={() => setCourseModalOpen(false)}>&times;</button>
              <h2 className="text-lg font-bold mb-4">Add New Course</h2>
              <input
                type="text"
                className="w-full border rounded-lg p-2 mb-3"
                placeholder="Course Name"
                value={newCourseName}
                onChange={e => setNewCourseName(e.target.value)}
              />
              {courseError && <div className="text-red-600 mb-2">{courseError}</div>}
              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  onClick={() => setCourseModalOpen(false)}
                >Cancel</button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={addCourse}
                >Add Course</button>
              </div>
            </div>
          </div>
        )}

        {/* Message Modal */}
        {messageModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div
              className="bg-white w-full h-full max-w-full min-h-screen rounded-none p-2 sm:rounded-xl sm:max-w-md sm:p-6 shadow-lg relative flex flex-col justify-center message-modal"
            >
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={closeMessageModal}>&times;</button>
              <h2 className="text-lg font-bold mb-4">
                Send Message to {messageRecipientType === 'student' ? 'Student' : 'Employee'}
              </h2>
              <textarea
                className="w-full border rounded-lg p-2 mb-4 text-base"
                rows={4}
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder="Type your message..."
                disabled={messageLoading || messageSuccess}
              />
              <div className="flex flex-col sm:flex-row justify-end gap-2 w-full">
                <button
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 w-full sm:w-auto"
                  onClick={closeMessageModal}
                  disabled={messageLoading}
                >Cancel</button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 w-full sm:w-auto"
                  onClick={sendMessage}
                  disabled={messageLoading || !messageText.trim()}
                >{messageLoading ? 'Sending...' : messageSuccess ? 'Sent!' : 'Send'}</button>
              </div>
              {messageSuccess && <div className="text-green-600 mt-2">Message sent successfully!</div>}
            </div>
          </div>
        )}

        {showOutlineModal && selectedCourse && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={closeOutlineModal}>
            <div className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] flex flex-col relative" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
                <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold" onClick={closeOutlineModal}>&times;</button>
                <h2 className="text-lg font-bold text-gray-800 pr-8">{selectedCourse.name} Outlines</h2>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 pt-4">
                <ol className="space-y-3 list-decimal list-inside">
                  {(selectedCourse.outlines || []).length === 0 && (
                    <li className="text-gray-400 italic">No outlines yet.</li>
                  )}
                  {(selectedCourse.outlines || []).map((outline, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <span className="font-semibold text-purple-700 flex-shrink-0">{idx + 1}.</span>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={adminCheckedOutlines.includes(idx)}
                          disabled={adminCheckedOutlines.includes(idx)}
                          onChange={() => handleAdminCheckOutline(idx)}
                          className="accent-purple-600 flex-shrink-0"
                        />
                        <span
                          className={`text-gray-800 text-sm flex-1 break-words ${
                            adminCheckedOutlines.includes(idx) ? "line-through text-gray-400" : ""
                          }`}
                        >
                          {outline}
                        </span>
                        <button 
                          onClick={() => deleteOutline(idx)} 
                          className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors flex-shrink-0" 
                          disabled={outlineLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              
              {/* Footer - Add New Outline */}
              <div className="p-6 pt-4 border-t border-gray-200 flex-shrink-0">
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 border rounded-lg p-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Add new outline"
                    value={outlineInput}
                    onChange={e => setOutlineInput(e.target.value)}
                    disabled={outlineLoading}
                  />
                  <button
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    onClick={addOutline}
                    disabled={outlineLoading || !outlineInput.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showDeleteStudentModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={() => setShowDeleteStudentModal(false)}>&times;</button>
              <h2 className="text-lg font-bold mb-4 text-red-600">Delete Student</h2>
              <p className="mb-6 text-gray-700">
                Are you sure you want to delete this student?<br/>
                <span className='font-semibold text-red-600'>This action cannot be undone.</span>
              </p>
              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setShowDeleteStudentModal(false)}>Cancel</button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  onClick={async () => {
                    await handleDeleteStudent(studentToDelete);
                    setShowDeleteStudentModal(false);
                    setStudentToDelete(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteEmployeeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={() => setShowDeleteEmployeeModal(false)}>&times;</button>
              <h2 className="text-lg font-bold mb-4 text-red-600">Delete Employee</h2>
              <p className="mb-6 text-gray-700">
                Are you sure you want to delete this employee?<br/>
                <span className='font-semibold text-red-600'>This action cannot be undone.</span>
              </p>
              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setShowDeleteEmployeeModal(false)}>Cancel</button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  onClick={async () => {
                    await handleDelete(employeeToDelete);
                    setShowDeleteEmployeeModal(false);
                    setEmployeeToDelete(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        {showDisableStudentModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={() => setShowDisableStudentModal(false)}>&times;</button>
              <h2 className="text-lg font-bold mb-4 text-orange-600">Disable Student</h2>
              <p className="mb-6 text-gray-700">
                Are you sure you want to disable <span className="font-semibold">{studentToDisable?.fullName}</span>?<br/>
                <span className='font-semibold text-orange-600'>This will prevent them from logging into the portal.</span>
              </p>
              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setShowDisableStudentModal(false)}>Cancel</button>
                <button
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                  onClick={() => handleDisableStudent(studentToDisable)}
                >
                  Disable
                </button>
              </div>
            </div>
          </div>
        )}
        {showDisableEmployeeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={() => setShowDisableEmployeeModal(false)}>&times;</button>
              <h2 className="text-lg font-bold mb-4 text-orange-600">Disable Employee</h2>
              <p className="mb-6 text-gray-700">
                Are you sure you want to disable <span className="font-semibold">{employeeToDisable?.fullName}</span>?<br/>
                <span className='font-semibold text-orange-600'>This will prevent them from logging into the portal.</span>
              </p>
              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setShowDisableEmployeeModal(false)}>Cancel</button>
                <button
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                  onClick={() => handleDisableEmployee(employeeToDisable)}
                >
                  Disable
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Client Modal */}
        {clientModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingClient ? 'Edit Client' : 'Add New Client'}
                </h2>
                <button
                  onClick={closeClientModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={(e) => { e.preventDefault(); editingClient ? updateClient() : addClient(); }}>
                {/* Client and Project Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Client & Project Details */}
                  <div className="space-y-6">
                    {/* Client Information Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 shadow-sm">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Client Information</h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Client Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newClient.name}
                            onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
                            placeholder="Enter client's full name"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Project Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newClient.projectName}
                            onChange={(e) => setNewClient({...newClient, projectName: e.target.value})}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
                            placeholder="Enter project name"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Project Timeline Section */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100 shadow-sm">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Project Timeline</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Receive Date</label>
                          <input
                            type="date"
                            value={newClient.projectReceiveDate}
                            onChange={(e) => setNewClient({...newClient, projectReceiveDate: e.target.value})}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Deadline</label>
                          <input
                            type="date"
                            value={newClient.deadline}
                            onChange={(e) => setNewClient({...newClient, deadline: e.target.value})}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column - Financial & Assignment */}
                  <div className="space-y-6">
                    {/* Financial Information Section */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 shadow-sm">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Financial Details</h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Project Cost</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">Rs</span>
                            <input
                              type="number"
                              value={newClient.projectCost}
                              onChange={(e) => setNewClient({...newClient, projectCost: e.target.value})}
                              className="w-full pl-8 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 shadow-sm"
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Developer Pay</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">Rs</span>
                            <input
                              type="number"
                              value={newClient.developerPay}
                              onChange={(e) => setNewClient({...newClient, developerPay: e.target.value})}
                              className="w-full pl-8 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 shadow-sm"
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Assignment Section */}
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-100 shadow-sm">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl flex items-center justify-center mr-3">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Assignment</h3>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Assigned To</label>
                        <input
                          type="text"
                          value={newClient.assignedTo}
                          onChange={(e) => setNewClient({...newClient, assignedTo: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 shadow-sm"
                          placeholder="Enter developer/team member name"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Description and Notes Section */}
                <div className="mt-8 bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-gradient-to-r from-gray-500 to-slate-600 rounded-xl flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Project Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Project Description</label>
                      <textarea
                        value={newClient.description}
                        onChange={(e) => setNewClient({...newClient, description: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 shadow-sm resize-none"
                        rows="4"
                        placeholder="Enter detailed project description..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Notes</label>
                      <textarea
                        value={newClient.notes}
                        onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 shadow-sm resize-none"
                        rows="4"
                        placeholder="Enter any additional notes or special requirements..."
                      />
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeClientModal}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                  >
                    {editingClient ? 'Update Client' : 'Add Client'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Client Details Modal */}
        {clientDetailsModalOpen && selectedClient && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">{selectedClient.name}</h2>
                      <p className="text-gray-600">{selectedClient.projectName}</p>
                    </div>
                  </div>
                  <button
                    onClick={closeClientDetailsModal}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Project Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Basic Info */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Basic Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Client Name:</span>
                        <span className="text-gray-800 font-semibold">{selectedClient.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Project Name:</span>
                        <span className="text-gray-800 font-semibold">{selectedClient.projectName}</span>
                      </div>
                      {selectedClient.assignedTo && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Assigned To:</span>
                          <span className="text-gray-800 font-semibold">{selectedClient.assignedTo}</span>
                        </div>
                      )}
                      {selectedClient.projectReceiveDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Received Date:</span>
                          <span className="text-gray-800 font-semibold">{new Date(selectedClient.projectReceiveDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {selectedClient.deadline && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Deadline:</span>
                          <span className="text-gray-800 font-semibold">{new Date(selectedClient.deadline).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Financial Info */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                      Financial Details
                    </h3>
                    <div className="space-y-3">
                      {selectedClient.projectCost && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Project Cost:</span>
                          <span className="text-green-600 font-bold">Rs. {selectedClient.projectCost.toLocaleString()}</span>
                        </div>
                      )}
                      {selectedClient.developerPay && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 font-medium">Developer Payment:</span>
                          <span className="text-red-600 font-bold">Rs. {selectedClient.developerPay.toLocaleString()}</span>
                        </div>
                      )}
                      {selectedClient.projectCost && selectedClient.developerPay && (
                        <div className="flex justify-between pt-2 border-t border-green-200">
                          <span className="text-gray-800 font-bold">Net Profit:</span>
                          <span className={`font-bold ${(selectedClient.projectCost - selectedClient.developerPay) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            Rs. {(selectedClient.projectCost - selectedClient.developerPay).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Project Description */}
                {selectedClient.description && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100 mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Project Description
                    </h3>
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedClient.description}</p>
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {selectedClient.notes && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100 mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Additional Notes
                    </h3>
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedClient.notes}</p>
                    </div>
                  </div>
                )}



                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={closeClientDetailsModal}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Modal */}
        <QuizModal
          isOpen={quizModalOpen}
          onClose={() => {
            setQuizModalOpen(false);
            setSelectedQuizCourse(null);
          }}
          course={selectedQuizCourse}
        />
      </div>
      
  
  );
}

export default AdminDashboard;
